<script setup lang="ts">
import type { RouteLocationRaw } from "vue-router";

import Link from "../buttons/Link.vue";
import Toggle from "./Toggle.vue";

interface ToggleLabelProps {
    active: boolean;
    label: string;
    description?: string;
    size?: "md" | "sm";
    disabled?: boolean;
    linkButtonText?: string;
    linkButtonTo?: RouteLocationRaw;
    underline?: boolean;
}

withDefaults(defineProps<ToggleLabelProps>(), {
    size: "md",
    disabled: false,
    underline: false,
});

const emit = defineEmits(["input"]);

const input = () => {
    emit("input");
};
</script>
<template>
    <div :class="['ds-toggle-label', size, { disabled, active }]" @click="input">
        <Toggle class="toggle" :size="size" :active="active" :disabled="disabled" />
        <span class="toggle-label regular">{{ label }}</span>
        <span v-if="description" class="toggle-description regular">{{ description }}</span>
        <Link
            class="toggle-link-button"
            type="primary"
            :size="size"
            :underline="underline"
            :disabled="disabled"
            :label="linkButtonText"
            :to="linkButtonTo"
        />
    </div>
</template>
<style scoped lang="scss">
.ds-toggle-label {
    column-gap: 8px;
    width: min-content;
    cursor: pointer;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;

    .toggle {
        grid-column: 1 / span 1;
    }

    .toggle-label,
    .toggle-description,
    .toggle-link-button {
        grid-column: 2 / span 1;
        white-space: nowrap;
    }

    .toggle-label {
        color: $text-sub-600;
        @include font-size-16;
        transition: 0.2s;
        font-weight: 400;
        user-select: none;
    }

    .toggle-description {
        color: $text-soft-400;
        @include font-size-14;
        transition: 0.2s;
        font-weight: 400;
    }

    &.sm {
        .toggle-label {
            @include font-size-14;
        }

        .toggle-description {
            @include font-size-13;
        }
    }

    &.active {
        .toggle-label {
            color: $text-main-900;
        }

        .toggle-description {
            color: $text-sub-600;
        }
    }

    &:hover {
        :deep(.ds-toggle) {
            .toggle-wrapper {
                background-color: $stroke-sub-300;
            }
            &.active {
                .toggle-wrapper {
                    background-color: $blue-dark;
                }
            }
        }

        .toggle-label {
            color: $text-main-900;
        }
        .toggle-description {
            color: $text-sub-600;
        }
    }

    &.disabled {
        cursor: default;
        pointer-events: none;

        :deep(.ds-toggle) {
            .toggle-wrapper {
                background-color: $bg-weak-100;
            }
        }
        .toggle-label {
            color: $text-disabled-300;
        }
        .toggle-description {
            color: $text-disabled-300;
        }
    }
}
</style>
